import { type AnchorHTMLAttributes, type HTMLAttributes, type MouseEvent, type ReactNode, forwardRef } from 'react';

import { cn } from '../utils/cn';

export interface NavLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   * Whether the link is active
   * @default false
   */
  active?: boolean;

  /**
   * Link variant
   * @default 'default'
   */
  variant?: 'default' | 'sidebar' | 'tabs' | 'pills';

  /**
   * Icon to display before text
   */
  icon?: ReactNode;

  /**
   * Icon to display after text
   */
  iconAfter?: ReactNode;

  /**
   * Badge to display (e.g., count, "new" indicator)
   */
  badge?: ReactNode;

  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
}

/**
 * NavLink component
 *
 * Navigation link with support for active states and multiple variants.
 * Can be used in headers, sidebars, tabs, and other navigation contexts.
 *
 * @example
 * ```tsx
 * <NavLink href="/dashboard" active icon={<HomeIcon />}>
 *   Dashboard
 * </NavLink>
 *
 * <NavLink href="/messages" badge={<Badge>5</Badge>}>
 *   Messages
 * </NavLink>
 * ```
 */
export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  (
    {
      active = false,
      variant = 'default',
      icon,
      iconAfter,
      badge,
      disabled = false,
      className,
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      onClick?.(e);
    };

    const baseStyles = [
      'inline-flex items-center gap-2',
      'font-medium transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded',
    ];

    const variantStyles = {
      default: active
        ? 'text-primary-600 hover:text-primary-700'
        : 'text-gray-700 hover:text-gray-900',
      sidebar: cn(
        'w-full px-3 py-2 rounded-lg text-sm',
        active ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
      ),
      tabs: cn(
        'px-4 py-2 border-b-2 text-sm',
        active
          ? 'border-primary-600 text-primary-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      ),
      pills: cn(
        'px-4 py-2 rounded-full text-sm',
        active
          ? 'bg-primary-600 text-white hover:bg-primary-700'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      ),
    };

    const disabledStyles = disabled
      ? 'opacity-50 cursor-not-allowed pointer-events-none'
      : 'cursor-pointer';

    return (
      <a
        ref={ref}
        className={cn(baseStyles, variantStyles[variant], disabledStyles, className)}
        aria-current={active ? 'page' : undefined}
        aria-disabled={disabled}
        onClick={handleClick}
        {...props}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span className="flex-1">{children}</span>
        {badge && <span className="flex-shrink-0">{badge}</span>}
        {iconAfter && <span className="flex-shrink-0">{iconAfter}</span>}
      </a>
    );
  }
);

NavLink.displayName = 'NavLink';

export interface NavGroupProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Group label
   */
  label?: string;

  /**
   * Navigation items
   */
  children: ReactNode;
}

/**
 * NavGroup component
 *
 * Group multiple navigation links with an optional label.
 *
 * @example
 * ```tsx
 * <NavGroup label="Main">
 *   <NavLink href="/">Home</NavLink>
 *   <NavLink href="/about">About</NavLink>
 * </NavGroup>
 * ```
 */
export const NavGroup = forwardRef<HTMLDivElement, NavGroupProps>(
  ({ label, className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('space-y-1', className)} {...props}>
        {label && (
          <div className="px-3 mb-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {label}
            </h3>
          </div>
        )}
        <nav className="space-y-1">{children}</nav>
      </div>
    );
  }
);

NavGroup.displayName = 'NavGroup';
